import type {MutableRefObject} from 'react';
import type {Key, Selection} from '@react-types/shared';
import type {ListCollection} from '@sail/react-aria';
import type {SelectionProps} from '../SelectField';
import type {FauxTreeGroupProps} from '../FauxTreeGroup';
import type {FauxTreeItemProps} from '../FauxTreeItem';

const getFirstKeyFromSelection = (
  selection: 'all' | Iterable<Key> | undefined,
  collection: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>,
): Key | undefined => {
  let allKeys: Selection = new Set();
  if (selection === 'all') {
    allKeys = new Set(collection.getItems().map(({key}) => key));
  } else if (selection) {
    allKeys = new Set(selection);
  }

  return Array.from(allKeys)[0];
};

const isValidSelectedKey = (
  key: Key | null | undefined,
): key is Key | undefined => key !== null && key !== '';

export const normalizeSelectionProps = (
  props: SelectionProps,
  collection: ListCollection<FauxTreeItemProps, FauxTreeGroupProps>,
  canCallOnSelectionChangeWithNullKey = false,
  currentSelectedKeysRef:
    | MutableRefObject<Iterable<Key> | undefined>
    | undefined = undefined,
): {
  selectedKeys: Iterable<Key> | undefined;
  defaultSelectedKeys: Iterable<Key> | undefined;
  onSelectionChange: (keys: Selection) => void;
} => {
  const isSelectedKeyProvided =
    'selectedKey' in props && props.selectedKey !== undefined;
  const isDefaultSelectedKeyProvided =
    'defaultSelectedKey' in props && props.defaultSelectedKey !== undefined;
  const isSelectedKeysProvided =
    'selectedKeys' in props && !!props.selectedKeys;
  const isDefaultSelectedKeysProvided =
    'defaultSelectedKeys' in props && !!props.defaultSelectedKeys;

  if (props.selectionMode === 'multiple') {
    const selectedKeys =
      !isSelectedKeysProvided &&
      isSelectedKeyProvided &&
      isValidSelectedKey(props.selectedKey)
        ? new Set<Key>([props.selectedKey!])
        : props.selectedKeys;
    const defaultSelectedKeys =
      !isDefaultSelectedKeysProvided &&
      isDefaultSelectedKeyProvided &&
      isValidSelectedKey(props.defaultSelectedKey)
        ? new Set<Key>([props.defaultSelectedKey!])
        : props.defaultSelectedKeys;

    return {
      selectedKeys,
      defaultSelectedKeys,
      onSelectionChange: (keys: Selection) =>
        props.onMultiSelectionChange?.(keys),
    };
  } else {
    const selectedKey =
      !isSelectedKeyProvided && isSelectedKeysProvided
        ? getFirstKeyFromSelection(props.selectedKeys, collection)
        : props.selectedKey;

    const defaultSelectedKey =
      !isDefaultSelectedKeyProvided && isDefaultSelectedKeysProvided
        ? getFirstKeyFromSelection(props.defaultSelectedKeys, collection)
        : props.defaultSelectedKey;

    return {
      selectedKeys: (() => {
        if (selectedKey !== undefined) {
          return selectedKey !== null ? new Set([selectedKey]) : new Set(); // When selectedKey is null, we need to return an empty set to have it considered a controlled value
        }

        return undefined;
      })(),
      defaultSelectedKeys: defaultSelectedKey
        ? new Set([defaultSelectedKey])
        : undefined,
      onSelectionChange: (keys: Selection) => {
        if (props.onSelectionChange) {
          const firstKey = Array.from(keys)[0] ?? null;
          const currentFirstKey = currentSelectedKeysRef?.current
            ? Array.from(currentSelectedKeysRef.current)[0] ?? null
            : undefined;

          if (
            currentFirstKey !== firstKey &&
            (canCallOnSelectionChangeWithNullKey ||
              isValidSelectedKey(firstKey))
          ) {
            props.onSelectionChange(firstKey);
          }
        }
      },
    };
  }
};

export const validateSelectionProps = (props: SelectionProps) => {
  if (process.env.NODE_ENV !== 'production') {
    if (
      props.selectionMode !== 'multiple' &&
      ('selectedKeys' in props || 'defaultSelectedKeys' in props)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `SelectField: "selectedKeys" and "defaultSelectedKeys" should not be used with selectionMode="${props.selectionMode}"`,
      );
    }

    if (
      props.selectionMode === 'multiple' &&
      ('selectedKey' in props || 'defaultSelectedKey' in props)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        'SelectField: "selectedKey" and "defaultSelectedKey" should not be used with selectionMode="multiple"',
      );
    }
  }
};
